import { ReactNode, useCallback } from 'react'

import styled from '@emotion/styled'
import { format } from 'date-fns'
import { DayPicker, DayPickerSingleProps } from 'react-day-picker'

import { TickerUpIcon } from '@arcade/web/icons/react'

import { useHelpTextId } from '../../hooks/useHelpTextId'
import ArcadeHelpText, { ArcadeHelpTextComponentProps, combineAriaDescribedBy } from '../Input/ArcadeHelpText'
import { ArcadeDatePickerClassNames } from './ArcadeDatePickerClassNames'
import { ArcadeDatePickerDropdown, ArcadeDatePickerDropdownProps } from './ArcadeDatePickerDropdown'
import { DatePickerCaption } from './DatePickerCaption'
import { Copy, defaultCopy } from './DatePickerCopy'
import { DatePickerDialog, DialogFocusContext } from './DatePickerDialog'
import { DatePickerDropdownContextProvider } from './DatePickerDropdownContext'

const Container = styled.section`
  border-radius: ${props => props.theme.border.radius.medium};
  background-color: ${props => props.theme.arcadeColors.semantic.backgroundApp};
  padding: ${props => props.theme.spacing.default};
  border: ${props => props.theme.pxToRem(1)} solid ${props => props.theme.arcadeColors.semantic.borderSubtle};
  user-select: none;
  grid-template-rows: max-content max-content;
  width: ${props => props.theme.pxToRem(300)};
  box-shadow: ${props => props.theme.dropShadows.dialog};
`

const NavIcon = () => <TickerUpIcon size={24} />

type CustomProps = {
  copy?: Copy
  open?: boolean
  closeOnSelect?: boolean
  onClose?: () => void
  children?: (targetProps: { 'aria-haspopup': 'dialog' }) => ReactNode
} & Pick<ArcadeHelpTextComponentProps, 'helpText' | 'HelpTextIcon' | 'errorText' | 'isInvalid'>

export type DatePickerProps = CustomProps &
  Omit<
    DayPickerSingleProps,
    | 'captionLayout'
    | 'className'
    | 'classNames'
    | 'components'
    | 'formatters'
    | 'labels'
    | 'locale'
    | 'mode'
    | 'numberOfMonths'
    | 'styles'
  > & {
    'aria-describedby'?: string
  }

const getRangeStartFallback = (props: DatePickerProps) => {
  return [props.fromYear, props.fromMonth, props.fromDate].some(value => value != null)
    ? {}
    : { fromYear: new Date().getFullYear() - 3 }
}

const getRangeEndFallback = (props: DatePickerProps) => {
  return [props.toYear, props.toMonth, props.toDate].some(value => value != null)
    ? {}
    : { toYear: new Date().getFullYear() }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const ArcadeDatePicker = ({
  onClose,
  copy = defaultCopy,
  open = false,
  closeOnSelect,
  children,
  helpText,
  HelpTextIcon,
  errorText,
  isInvalid = false,
  'aria-describedby': ariaDescribedBy,
  ...props
}: DatePickerProps) => {
  const helpTextId = useHelpTextId({ helpText, errorText })

  // Memoize the dropdown component to prevent recreation on every render
  const DatePickerDropdown = useCallback(
    (props: ArcadeDatePickerDropdownProps) => (
      <ArcadeDatePickerDropdown {...props} aria-describedby={combineAriaDescribedBy(ariaDescribedBy, helpTextId)} />
    ),
    [ariaDescribedBy, helpTextId]
  )

  const renderPicker = () => (
    <>
      <Container>
        <DialogFocusContext.Consumer>
          {({ returnFocus }) => (
            <DatePickerDropdownContextProvider>
              {({ close: closeOpenDropdown }) => (
                <ArcadeDatePickerClassNames>
                  {classNames => (
                    <DayPicker
                      {...props}
                      {...getRangeStartFallback(props)}
                      {...getRangeEndFallback(props)}
                      mode="single"
                      locale={copy.locale}
                      captionLayout="dropdown-buttons"
                      classNames={classNames}
                      components={{
                        Caption: DatePickerCaption,
                        Dropdown: DatePickerDropdown,
                        IconRight: NavIcon,
                        IconLeft: NavIcon,
                      }}
                      formatters={{
                        formatMonthCaption: (date, options) => format(date, 'MMM', { locale: options?.locale }),
                        formatWeekdayName: (date, options) => format(date, 'EEEEE', { locale: options?.locale }),
                      }}
                      labels={{
                        labelMonthDropdown: () => copy.dropdowns.monthLabel,
                        labelYearDropdown: () => copy.dropdowns.yearLabel,
                        labelNext: () => copy.navigation.nextLabel,
                        labelPrevious: () => copy.navigation.previousLabel,
                      }}
                      onMonthChange={(...args) => {
                        closeOpenDropdown()
                        props.onMonthChange?.(...args)
                      }}
                      onSelect={async (...args) => {
                        props.onSelect?.(...args)
                        if (closeOnSelect) {
                          const transitionDelay = 80
                          await delay(transitionDelay)
                          onClose?.()
                          returnFocus()
                        }
                      }}
                    />
                  )}
                </ArcadeDatePickerClassNames>
              )}
            </DatePickerDropdownContextProvider>
          )}
        </DialogFocusContext.Consumer>
      </Container>
      {/** DatePicker does not support a disabled state, so we don't need to pass through disabled text. */}
      <ArcadeHelpText
        helpTextId={helpTextId}
        helpText={helpText}
        HelpTextIcon={HelpTextIcon}
        isInvalid={isInvalid}
        errorText={errorText}
        disabled={false}
      />
    </>
  )

  if (!children) {
    return renderPicker()
  }

  return <DatePickerDialog open={open} onClose={onClose} renderPicker={renderPicker} renderTarget={children} />
}

export default ArcadeDatePicker
